import {
  products,
  productLines,
  productFamilies,
  productGroups,
  collapsedGroupOfFamily,
  collapsedProductOfGroup,
  type Product,
  type ProductFamily,
  type ProductGroup,
} from "@/lib/data";

/**
 * Resolve a product to its full canonical URL — `/produtos/{line}/{family}/{group}/{id}`.
 *
 * `product.id` IS the slug (api.ts maps it from `p.slug ?? String(p.id)`),
 * and the static catalogue's `id` field is the kebab-case slug, so this
 * URL is stable across CMS-served and static-fallback data.
 *
 * Returns `null` if any part of the breadcrumb chain (line/family/group)
 * is missing, which would mean a partially-orphaned record. Callers
 * typically suppress the link in that case rather than rendering a 404.
 *
 * Centralised here because the same look-up was duplicated across
 * ProductCard, ProductDetail's discontinued banner, AccessoriesSection,
 * CrossSellSection, ProductGroupModels — each with its own subtle
 * variant. The duplication caused real bugs: the discontinued banner
 * built the URL using the SOURCE product's line/family/group instead of
 * the REPLACEMENT product's, so swapping a product across groups
 * silently 404'd the link.
 */
export function getProductUrl(product: Product): string | null {
  const group = productGroups.find((g) => g.id === product.groupId);
  if (!group) return null;
  const family = productFamilies.find((f) => f.id === group.familyId);
  if (!family) return null;
  const line = productLines.find((l) => l.id === family.lineId);
  if (!line) return null;
  return `/produtos/${line.slug}/${family.slug}/${group.slug}/${product.id}`;
}

/**
 * Where a family link should actually land.
 *
 * Normally the family page. But a family whose page would be a single group
 * card repeating its own name has nothing to show, so it collapses — and if
 * that group in turn collapses to its single product, this walks all the way
 * down. "Pinos de Solda Capacitiva" (family = group = product, one name)
 * therefore goes straight to the product instead of via two pages that each
 * just say the name again.
 *
 * Every family link runs through here — the home tiles, the line page, the
 * header menu — so a link and the redirect on ProductFamily can't disagree
 * about the destination.
 */
export function getFamilyUrl(family: Pick<ProductFamily, "id" | "slug" | "lineId">): string | null {
  const line = productLines.find((l) => l.id === family.lineId);

  // No line, no URL. This used to default to "fixadores", which turned a
  // caller passing an incomplete family into a plausible-looking link to the
  // wrong product line — every Ferramentas tile pointed at
  // /produtos/fixadores/… and rendered "Família não encontrada". Returning
  // null makes the caller fall back to the path it already knows, so the
  // failure is a missing collapse rather than a broken link.
  if (!line) return null;

  // Services aren't under /produtos at all — /produtos/servicos/<slug> is a
  // legacy path that 301s to /servicos/<slug> (see routes-client). Emit the
  // canonical one so links don't bounce through a redirect.
  if (line.slug === "servicos") return `/servicos/${family.slug}`;

  const base = `/produtos/${line.slug}/${family.slug}`;
  const group = collapsedGroupOfFamily(family.id);
  if (!group) return base;
  const product = collapsedProductOfGroup(group.id);
  return product ? `${base}/${group.slug}/${product.id}` : `${base}/${group.slug}`;
}

/**
 * Where a group link should land: the group page, or — when the group is a
 * single product wearing the group's own name — that product, matching the
 * redirect ProductGroup performs (see collapsedProductOfGroup). Used by search
 * so a "Grupos" hit never sends the user through a page that only bounces.
 */
export function getGroupUrl(group: Pick<ProductGroup, "id" | "slug" | "familyId">): string | null {
  const family = productFamilies.find((f) => f.id === group.familyId);
  if (!family) return null;
  const line = productLines.find((l) => l.id === family.lineId);
  if (!line) return null;
  const base = `/produtos/${line.slug}/${family.slug}/${group.slug}`;
  const product = collapsedProductOfGroup(group.id);
  return product ? `${base}/${product.id}` : base;
}

/**
 * Look up a product by slug. Slug == id since the SKU drop — both CMS
 * and static catalogue use the kebab-case identifier as the URL token.
 */
export function findProductBySlug(slug: string): Product | undefined {
  return products.find((p) => p.id === slug);
}

/**
 * Resolve a replacement reference (the `discontinuedReplacement`
 * pulled off the api shape: `{slug, name}`) to a full URL — looking
 * up the actual product record in the catalogue so the URL is built
 * from the REPLACEMENT'S group/family/line, not the discontinued
 * source's.
 *
 * Returns `null` when the slug doesn't resolve to a known product
 * (orphaned reference) — caller should drop the link.
 */
export function getReplacementUrl(ref: { slug: string; name: string } | undefined): string | null {
  if (!ref) return null;
  const product = findProductBySlug(ref.slug);
  if (!product) return null;
  return getProductUrl(product);
}
